import React from 'react';
import { useCurrentRequest, useRequestsCounts } from 'src/models';
import { useTranslation } from 'react-i18next';
import { If } from 'src/components/If';
 
import { useToast } from 'src/components/ui/use-toast';
import { generatePath, Link, useLocation, useNavigate } from 'react-router-dom';
import { appRoutes } from 'src/routes';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useRequestMutations } from 'src/features/requests/use-request-mutations';
import { Button } from 'src/components/ui/button/button';
import { ChevronLeft, Video } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/components/ui/breadcrumb';
import { PageHeading, PageHeadingTitle } from 'src/components/ui/page-heading';
import { useConfirm } from 'src/components/ConfirmModal';
import { ButtonLoader } from 'src/components/ui/loader';
 

const RevisionsBreadcrumbs: React.FC = () => {
  const { t } = useTranslation(['pages/shared', 'alerts']);
  const navigate = useNavigate();
  const location = useLocation();
  const { title } = useCurrentRequest();

  return (
    <PageHeading>
      <Breadcrumb>
        <BreadcrumbList className={'tw-flex-nowrap'}>
          <BreadcrumbItem>
            <Button
              variant={'unset'}
              size={'iconXs'}
              onClick={() => {
                if (location.key !== 'default') {
                  history.back();
                } else {
                  navigate(appRoutes.requestsList);
                }
              }}
            >
              <ChevronLeft className={'tw-size-4'} />
            </Button>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.dashboard}>
                {t('common:dashboard', {
                  defaultValue: 'Dashboard',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.requestsList}>
                {t('common:requests', {
                  defaultValue: 'Requests',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbPage>
            {t('common:revisions', {
              defaultValue: 'Revisions',
            })}
          </BreadcrumbPage>
        </BreadcrumbList>
      </Breadcrumb>
      <PageHeadingTitle className={'tw-flex-nowrap'}>
        <div>
          <Video className={'tw-size-6'} />
        </div>
        <span className={'tw-line-clamp-1'}>
          {title || t('common:revision', { defaultValue: 'Request revision' })}
        </span>
      </PageHeadingTitle>
    </PageHeading>
  );
};

const OutcomeRevisionsBreadcrumbs: React.FC<{ title: string; parentId: string }> = ({
  title,
  parentId,
}) => {
  const { t } = useTranslation('pages/shared');
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <PageHeading>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <Button
              variant={'unset'}
              size={'iconXs'}
              onClick={() => {
                if (location.key !== 'default') {
                  history.back();
                } else {
                  navigate(generatePath(appRoutes.request, { requestId: parentId }));
                }
              }}
            >
              <ChevronLeft className={'tw-size-4'} />
            </Button>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.dashboard}>
                {t('common:dashboard', {
                  defaultValue: 'Dashboard',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.requestsList}>
                {t('common:requests', {
                  defaultValue: 'Requests',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={generatePath(appRoutes.request, { requestId: parentId })}>
                {t('common:Request', {
                  defaultValue: 'Request',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbPage>
            {t('common:revisions', {
              defaultValue: 'Revisions',
            })}
          </BreadcrumbPage>
        </BreadcrumbList>
      </Breadcrumb>
      <PageHeadingTitle className={'tw-flex-nowrap'}>
        <div>
          <Video className={'tw-size-6'} />
        </div>
        <span className={'tw-line-clamp-1'}>
          {title || t('common:revision', { defaultValue: 'Request revision' })}
        </span>
      </PageHeadingTitle>
    </PageHeading>
  );
};

const RevisionsContentTop: React.FC = () => {
  const { t } = useTranslation(['pages/shared', 'alerts']);
  const { confirm } = useConfirm();

  const request = useDecoratedRequestContext();
  const requestMutations = useRequestMutations();

  const { toast } = useToast();
  const { update } = useRequestsCounts(
    {},
    {
      enabled: false,
    },
  );
  

  const handleUndoReeditRequest = async () => {
    const confirmed = await confirm('', {
      modal_header: {
        content: t('Undo re-edit request')!,
      },
      modal_body: {
        content: t(
          'Undoing will remove it from the re-editing queue.',
        )!,
      },
    });

    if (!confirmed) {
      return;
    }

    await requestMutations.undoReeditRequest.mutateAsync(request.id, {
      onSuccess: () => {
        toast({
          description: t('Re-edit request has been undone.'),
        });
      },
    });
  };

  return (
    <div className={'tw-mb-4 tw-flex tw-flex-wrap tw-gap-4 md:tw-mb-10'}>
      <div
        className={
          'tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 md:tw-justify-start'
        }
      >
        <If when={request.flow === 'outcome'} else={<RevisionsBreadcrumbs />}>
          <OutcomeRevisionsBreadcrumbs title={request.title} parentId={request!.parent_id!} />
        </If>
      </div>

      <div className={'tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2'}>

        {request.needsReedits && request.isDelivered && (
          <Button
            variant={'outline'}
            size={'responsive'}
            disabled={requestMutations.isAnyMutationLoading}
            onClick={handleUndoReeditRequest}
          >
            {t('Undo re-edit request')}
            <ButtonLoader loading={requestMutations.undoReeditRequest.isLoading} />
          </Button>
        )}

        
      </div>
    </div>
  );
};

export { RevisionsContentTop };
